import React, {useCallback, useEffect} from "react";
import {inject, observer} from "mobx-react";
import cx from "classnames";

import {LLBtn2, LLDateRangePicker, LLPreloader, LLTable} from "../../../../components";
import {DrawerCloseButton} from "../../components";
import {trans} from "../../../../common/utils";

const AFFILIATE_TOTAL_COLUMNS = [
    'sportsProfit',
    'casinoProfit',
    'otherProfit',
    'bonus',
    'totalProfit',
];


const AffiliatePage = inject('store')(observer((
    {
        store: {
            user: {
                affiliates,
                affiliateUrl,
            },
            site : { status: {viewSize} }
        },
    }
) => {
    const {
        getAffiliates,
        items,
        isTableLoading,
    } = affiliates;

    useEffect(() => {
        getAffiliates();
    }, []);

    const copyToClipboard = useCallback((str) => {
        const el = document.createElement('textarea');
        el.value = str;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        const selected =
            document.getSelection().rangeCount > 0
                ? document.getSelection().getRangeAt(0)
                : false;
        el.select();
        const success = document.execCommand('copy');
        document.body.removeChild(el);
        if (selected) {
            document.getSelection().removeAllRanges();
            document.getSelection().addRange(selected);
        }
        return success;
    }, []);

    const affiliateTableConfig = {
        mobileGridProps: {
            noDataText: 'AFFILIATES_NO_DATA',
            paging: {
                enabled: false,
            },
        },
        mobile: {
            columns: [
                {
                    key: 'userName',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_USER_NAME'),
                },
                {
                    key: 'registrationDate',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_REGISTRATION_DATE'),
                },
                {
                    key: 'sportsProfit',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_SPORTS_PROFIT'),
                    format: 'currency',
                    alignment: 'right',
                },
                {
                    key: 'casinoProfit',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_CASINO_PROFIT'),
                    format: 'currency',
                    alignment: 'right',
                },
                {
                    key: 'otherProfit',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_OTHER_PROFIT'),
                    format: 'currency',
                    alignment: 'right',
                },
                {
                    key: 'bonus',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_BONUS'),
                    format: 'currency',
                    alignment: 'right',
                },
                {
                    key: 'totalProfit',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_TOTAL_PROFIT'),
                    format: 'currency',
                    alignment: 'right',
                },
            ],
            headerFilter: {
                visible: true,
            },
            totalColumnKeys: AFFILIATE_TOTAL_COLUMNS,
        },
        desktopGridProps: {
            noDataText: 'AFFILIATES_NO_DATA',
            paging: {
                enabled: false,
            },
        },
        desktop: {
            columns: [
                {
                    key: 'userName',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_USER_NAME'),
                },
                {
                    key: 'registrationDate',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_REGISTRATION_DATE'),
                },
                {
                    key: 'sportsProfit',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_SPORTS_PROFIT'),
                    format: 'currency',
                    alignment: 'right',
                    width: 230,
                },
                {
                    key: 'casinoProfit',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_CASINO_PROFIT'),
                    format: 'currency',
                    alignment: 'right',
                },
                {
                    key: 'otherProfit',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_OTHER_PROFIT'),
                    format: 'currency',
                    alignment: 'right',
                },
                {
                    key: 'bonus',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_BONUS'),
                    format: 'currency',
                    alignment: 'right',
                },
                {
                    key: 'totalProfit',
                    title: trans('AFFILIATES_TABLE_COLUMN_TITLE_TOTAL_PROFIT'),
                    format: 'currency',
                    alignment: 'right',
                },
            ],
            headerFilter: {
                visible: true,
            },
            totalColumnKeys: AFFILIATE_TOTAL_COLUMNS,
        },
    };

    const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));

    return (
        <div className="affiliates my-account__content-container">
            {viewSize === 'desktop' && (
                <p className="my-account__title">
                    <span className="my-account__title-text">
                        {trans('AFFILIATES_TITLE')}
                    </span>
                    <DrawerCloseButton />
                </p>
            )}

            <div className="affiliates__content">
                <div className="affiliates__link">
                    <p className="affiliates__link-title">{trans('Affiliate link')}</p>
                    <div className="affiliates__url">
                        <div>{affiliateUrl}</div>
                        <LLBtn2
                            customClasses={cx('ll-btn--accent')}
                            type="button"
                            onClick={() => copyToClipboard(affiliateUrl)}
                        >
                            <span>{trans('Copy to clipboard')}</span>
                        </LLBtn2>
                    </div>
                </div>

                <p className="affiliates__note">
                    <span><i className="doruk-about-us-01"></i></span>
                    <span>{trans('AFFILIATES_NOTE')}</span>
                </p>
            </div>

            <div className="my-account__content">
                <div className="my-account__date-picker-wrapper">
                    <LLDateRangePicker
                        actionHandler={getAffiliates}
                        storeItem={affiliates}
                        absentRange={['Today']}
                        defaultDate={{
                            startDate: new Date(yesterday.setHours(0, 0, 0, 1)),
                            endDate: new Date(yesterday.setHours(23, 59, 59, 999)),
                        }}
                    />
                </div>

                <div className="my-account__table-box">
                    {isTableLoading ? (
                        <div className="my-account__loader-box">
                            <LLPreloader />
                        </div>
                    ) : (
                        <LLTable
                            name="affiliates"
                            data={items}
                            size={viewSize === 'mobile' ? 5 : 20}
                            customConfig={affiliateTableConfig}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}));

export default AffiliatePage;
